import { useCallback } from "react";
import useSWR, { useSWRConfig } from "swr";
import { AiccPromptGroup } from "constants/aiccPrompt";
import { AiccPromptList } from "types/model/aiccPrompt";
import * as aiccPromptAPI from "services/aiccPrompt";
import { AICC_SWR_CONFIG } from "./swrConfig";

const EMPTY_LIST: AiccPromptList = { versions: [], isLanguageDraftOpen: false };

const PROMPTS_KEY = "aicc/prompts";

/**
 * The prompts of one language, for one tab of the dashboard (AICC2-60).
 *
 * Grouped by readable copy like the other learning assets — the published version and the draft, in
 * that order — because a prompt is versioned by riding its flow's version. One group is the common
 * case: a draft exists only between Create Preview and the next publish.
 *
 * `isLanguageDraftOpen` comes back beside them because it belongs to the language rather than to a
 * copy: it is what says why every row can be read and none of them edited.
 *
 * The group is part of the key, so switching tabs is its own request and its own cache entry rather
 * than a filter over something already fetched — which is what keeps one tab from briefly showing the
 * previous tab's rows.
 *
 * No id means no request: the language comes from the store when the screen is opened from a
 * sub-asset's Manage button, and it is absent on the first render.
 */
export function useAiccPromptsOfGroup(languageId?: number, group?: AiccPromptGroup) {
  const { data, error, isLoading, mutate } = useSWR(
    languageId && group ? [PROMPTS_KEY, languageId, group] : null,
    () => aiccPromptAPI.fetchAiccPrompts(languageId as number, group),
    AICC_SWR_CONFIG
  );

  const promptList = data ?? EMPTY_LIST;

  return {
    versions: promptList.versions,
    isLanguageDraftOpen: promptList.isLanguageDraftOpen,
    isLoading,
    isError: !!error,
    mutate,
  };
}

/**
 * Drops every cached prompt list, for a writer on the detail page to call once its save lands.
 *
 * 🔴 REPORTED FROM DEV: change a prompt's model, save, go back — the row still named the old model.
 * `mutate` from `useAiccPrompt` only owns `["aicc/prompt", …]`; the list is a different key and nothing
 * touched it. Nor does going back fix itself: `AICC_SWR_CONFIG` sets `dedupingInterval: 5000`, so a
 * remount within five seconds of the last read — which is the normal speed of saving and clicking back —
 * is deduped away and the stale rows stay on screen with nothing scheduled to correct them.
 *
 * Every group, not just the one saved: a save recomputes the language's status, and `isLanguageDraftOpen`
 * rides on each group's response.
 *
 * Third argument present on purpose. `mutate(filter, undefined)` is two arguments, which SWR reads as
 * "no data supplied" and turns into a revalidate-only call — and a revalidate with no mounted hook
 * behind it leaves the cache exactly as it was. Passing the `undefined` explicitly is what clears it,
 * so the list refetches on mount instead of rendering last week's model first.
 */
export function useInvalidateAiccPrompts() {
  const { mutate } = useSWRConfig();

  return useCallback(
    () => mutate((key: unknown) => Array.isArray(key) && key[0] === PROMPTS_KEY, undefined, { revalidate: true }),
    [mutate]
  );
}
